/**
 * React Query hooks for Leads custom field definitions.
 */

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, {
  LeadsCreateCustomFieldInput,
  LeadsCustomFieldDefinition,
  LeadsCustomFieldEntityType,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

export function useCustomFields(entityType?: LeadsCustomFieldEntityType) {
  const queryClient = useQueryClient();

  const query = useQuery<{ fields: LeadsCustomFieldDefinition[] }>({
    queryKey: queryKeys.leads.customFields.list(entityType),
    queryFn: () => api.listLeadsCustomFields(entityType),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.leads.customFields.all() });

  const createMutation = useMutation({
    mutationFn: (input: LeadsCreateCustomFieldInput) => api.createLeadsCustomField(input),
    onSuccess: invalidate,
    onError: () =>
      toast({
        title: 'Error',
        description: 'Failed to create custom field',
        variant: 'destructive',
      }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<LeadsCreateCustomFieldInput> }) =>
      api.updateLeadsCustomField(id, input),
    onSuccess: invalidate,
    onError: () =>
      toast({
        title: 'Error',
        description: 'Failed to update custom field',
        variant: 'destructive',
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteLeadsCustomField(id),
    onSuccess: invalidate,
    onError: () =>
      toast({
        title: 'Error',
        description: 'Failed to delete custom field',
        variant: 'destructive',
      }),
  });

  useEffect(() => {
    if (query.isError && !query.isFetching) {
      toast({
        title: 'Error',
        description: 'Failed to load custom fields',
        variant: 'destructive',
      });
    }
  }, [query.isError, query.isFetching]);

  return {
    fields: query.data?.fields ?? [],
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
    createField: (input: LeadsCreateCustomFieldInput) => createMutation.mutateAsync(input),
    updateField: (id: string, input: Partial<LeadsCreateCustomFieldInput>) =>
      updateMutation.mutateAsync({ id, input }),
    deleteField: (id: string) => deleteMutation.mutateAsync(id),
  };
}
